/**
 * Tracked Routes Store - Pinia
 *
 * Single Source of Truth for all track data.
 * All components should use this store instead of direct DB calls.
 *
 * Migrated from VueX module src/store/modules/trackedRoutes/
 * Enhanced: 2025-12-31 - SSOT architecture
 */

import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import GeoMoverDB from '@/db/geoMoverDB';
import { useTrackedRouteVersionsStore } from './trackedRouteVersions';
import { useNotesStore } from './notes';
import { usePoisStore } from './pois';

export const useTrackedRoutesStore = defineStore('trackedRoutes', {
  state: () => ({
    // ========================================
    // NEW: Single Source of Truth for tracks
    // ========================================
    allTracks: [],
    loading: false,
    lastUpdated: null,

    // ========================================
    // LEGACY: Keep for backward compatibility
    // (will be removed in Phase 6)
    // ========================================
    allTrackedRoutes: '',
    showTrack: false,
    trackUpdatedSignal: null
  }),

  getters: {
    // ========================================
    // NEW: SSOT getters
    // ========================================

    /**
     * Total number of tracks
     */
    trackCount: (state) => state.allTracks.length,

    /**
     * Get a single track by ID
     * @returns {Function} - Getter function (id) => track | undefined
     */
    getTrackById: (state) => (id) =>
      state.allTracks.find(t => t.id === id),

    /**
     * Get paginated tracks for list views
     * @returns {Function} - Getter function (page, perPage) => tracks[]
     */
    getPaginatedTracks: (state) => (page, perPage) => {
      const start = page * perPage;
      return state.allTracks.slice(start, start + perPage);
    },

    /**
     * Check if tracks are loaded
     */
    isLoaded: (state) => state.lastUpdated !== null,

    // ========================================
    // LEGACY: Keep for backward compatibility
    // ========================================
    getAllTrackedRoutes: (state) => state.allTrackedRoutes,
    getShowTrack: (state) => state.showTrack,
    getTrackUpdatedSignal: (state) => state.trackUpdatedSignal
  },

  actions: {
    // ========================================
    // NEW: SSOT actions (DB + state atomic)
    // ========================================

    /**
     * Load all tracks from IndexedDB into store
     * Call this on app init and when full refresh is needed
     */
    async loadAllTracks () {
      this.loading = true;
      try {
        const tracks = await GeoMoverDB.findAll('trackedRoutes', 'date', 'desc');
        this.allTracks = tracks || [];
        this.lastUpdated = Date.now();

        // Keep legacy state in sync during migration
        this.allTrackedRoutes = this.allTracks;
      }
      finally {
        this.loading = false;
      }
    },

    /**
     * Add a new track: saves to DB and updates state
     * @param {Object} trackData - Track data to save
     * @returns {number} - The new track's ID
     */
    async addTrack (trackData) {
      const id = await GeoMoverDB.addOne('trackedRoutes', structuredClone(trackData));
      // Re-fetch to get proper ordering (new tracks go to top by date)
      await this.loadAllTracks();
      return id;
    },

    /**
     * Update an existing track: saves to DB and updates state
     * @param {number} id - Track ID to update
     * @param {Object} updates - Partial track data to merge
     */
    async updateTrack (id, updates) {
      const track = this.allTracks.find(t => t.id === id);
      if (!track) {
        throw new Error(`Track ${id} not found in store`);
      }

      const updated = { ...track, ...updates };
      await GeoMoverDB.updateOne('trackedRoutes', structuredClone({ ...toRaw(track), ...updates }), id);

      // Update in-memory state
      const index = this.allTracks.findIndex(t => t.id === id);
      if (index !== -1) {
        this.allTracks[index] = updated;
      }

      // Signal for components watching specific track changes
      this.trackUpdatedSignal = { trackId: id, timestamp: Date.now() };

      // Keep legacy state in sync
      this.allTrackedRoutes = this.allTracks;
    },

    /**
     * Delete a single track: removes from DB and state
     * @param {number} id - Track ID to delete
     */
    async deleteTrack (id) {
      const versionsStore = useTrackedRouteVersionsStore();
      const notesStore = useNotesStore();
      const poisStore = usePoisStore();
      await versionsStore.deleteVersionsForTrack(id);
      await notesStore.unlinkAllNotesForRoute(id);
      await poisStore.unlinkAllPoisForRoute(id);
      await GeoMoverDB.deleteOne('trackedRoutes', id);
      this.allTracks = this.allTracks.filter(t => t.id !== id);

      // Keep legacy state in sync
      this.allTrackedRoutes = this.allTracks;
    },

    /**
     * Delete multiple tracks: removes from DB and state (bulk operation)
     * @param {number[]} ids - Array of track IDs to delete
     */
    async deleteTracks (ids) {
      const versionsStore = useTrackedRouteVersionsStore();
      const notesStore = useNotesStore();
      const poisStore = usePoisStore();
      for (const id of ids) {
        await versionsStore.deleteVersionsForTrack(id);
        await notesStore.unlinkAllNotesForRoute(id);
        await poisStore.unlinkAllPoisForRoute(id);
        await GeoMoverDB.deleteOne('trackedRoutes', id);
      }
      this.allTracks = this.allTracks.filter(t => !ids.includes(t.id));

      // Keep legacy state in sync
      this.allTrackedRoutes = this.allTracks;
    },

    // ========================================
    // LEGACY: Keep for backward compatibility
    // (will be removed in Phase 6)
    // ========================================

    setAllTrackedRoutes (payload) {
      this.allTrackedRoutes = payload;
    },

    /**
     * @deprecated Use loadAllTracks() instead
     */
    async updateAllTrackedRoutes () {
      const data = await GeoMoverDB.findAll('trackedRoutes', 'date', 'desc');
      this.allTrackedRoutes = data;
      // Also update new state for components being migrated
      this.allTracks = data || [];
      this.lastUpdated = Date.now();
    },

    setShowTrack (bool) {
      this.showTrack = bool;
    },

    /**
     * Signal to notify components that a track was updated
     * @deprecated Actions now trigger this automatically
     */
    triggerTrackUpdated (trackId) {
      this.trackUpdatedSignal = { trackId, timestamp: Date.now() };
    }
  }
});
